/*
 * Node vocabulary
 *
 * Six types cover every branch in all five brand outlines. Adding a seventh
 * should feel like a decision, not a convenience. Each one is a renderer to
 * build, a validation rule to write, and a shape every future tree author has
 * to learn.
 *
 *   form      grouped fields, device facts, identity, address
 *   choice    single-select menu
 *   lookup    server call; branches on what it finds
 *   document  an answer. MAY offer an escalation, MAY offer a way onward
 *   escalate  enters the shared escalation sub-flow
 *   handoff   jumps into another brand's tree, carrying answers forward
 */

export const FORM = 'form';
export const CHOICE = 'choice';
export const LOOKUP = 'lookup';
export const DOCUMENT = 'document';
export const ESCALATE = 'escalate';
export const HANDOFF = 'handoff';
export const TYPES = [FORM, CHOICE, LOOKUP, DOCUMENT, ESCALATE, HANDOFF];

/*
 * Two more types exist, and neither is in TYPES because neither may appear in a
 * brand tree. They belong to the escalation sub-flow, which is code rather than
 * data, so no tree author can reach for them and the validator is right to
 * reject a tree that tries.
 *
 *   assistant a conversation with GRACI, seeded with the answers so far
 *   contact   a phone number to call instead of filling in the form
 *   submit    the end of the sub-flow, where the renderer sends
 */
export const ASSISTANT = 'assistant';
export const CONTACT = 'contact';
export const SUBMIT = 'submit';

/*
 * A terminal is a node the flow can legitimately come to rest on.
 *
 * `document` counts, which is the whole point of the correction that produced
 * it: a document is where most people should stop. It is only NOT the end when
 * the reader says it did not help, and that is their choice rather than the
 * tree's.
 */
export const TERMINALS = [DOCUMENT, ESCALATE];
export const isTerminal = node => TERMINALS.includes(node?.type);

/*
 * Every edge out of a node, as plain node ids. Used by the validator to walk the
 * graph and by the flow to know whether a node can be left at all.
 *
 * Guards count as edges. A node whose only exit is a guard is legitimate, the
 * escalation sub-flow's address step is exactly that, but it still has to
 * point somewhere real.
 */
export const edgesFrom = node => {
  if (!node) {
    return [];
  }
  let edges = [];
  (node.guard || []).forEach(guard => guard.goto && edges.push(guard.goto));
  if (node.next) {
    edges.push(node.next);
  }
  if (node.type === CHOICE) {
    (node.options || []).forEach(option => option.next && edges.push(option.next));
  }
  if (node.type === DOCUMENT) {
    if (node.continue?.next) {
      edges.push(node.continue.next);
    }
    // `escalate` leaves this graph for the escalation sub-flow, so it is
    // deliberately not an edge within the tree.
  }
  return edges;
};